import React, {
	Key,
	useMemo,
	Suspense,
	useEffect,
	useState,
	lazy,
} from "react";
import { useTranslation } from "react-i18next";

import Tariff from "../../../../services/Tariff";
import setRates from "../../../../redux/actions/Preferences/Rates/setRates";
import { useTypedDispatch, useTypedSelector } from "../../../../redux/store";
import useActiveTab from "../../../../hooks/useActiveTab";
import useModelSubscribe from "../../../../hooks/useModelSubscribe2";
import { hasAccess } from "../../../../access";
import { TabOptions } from "../../../../types";
import {
	StyledRow,
	SuspenseLoader,
	SideTab,
} from "../../../../components/common";
import BasicPageLayout from "../../../../components/BasicPageLayout";

import { TabKeys, TabAccessNames } from "./constants/access";

const lazyWithRetry = (factory: () => Promise<any>) =>
	new Promise((resolve, reject) => {
		factory()
			.then(resolve)
			.catch((error) => {
				console.warn("Chunk load failed, reloading...");
				window.location.reload();
			});
	});

const LazyOrders = lazy<React.FC<ArchivesPage.Props>>(async () => {
	const elem = await import(
		"./tabs/Orders" /* webpackChunkName: "archives-page-orders" */
	);
	return { default: elem.default };
});

const LazyGPSLog = lazy<React.FC<ArchivesPage.Props>>(async () => {
	const elem = await import(
		"./tabs/GPSLog" /* webpackChunkName: "archives-page-gps-log" */
	);
	return { default: elem.default };
});

const LazyMessagesHistory = lazy<React.FC<ArchivesPage.Props>>(async () => {
	const elem = await import(
		"./tabs/MessagesHistory" /* webpackChunkName: "archives-page-messages" */
	);
	return { default: elem.default };
});

const LazyPushHistory = lazy<React.FC<ArchivesPage.Props>>(async () => {
	const elem = await import(
		"./tabs/PushHistory" /* webpackChunkName: "archives-page-push" */
	);
	return { default: elem.default };
});

const LazyTransactions = lazy<React.FC<ArchivesPage.Props>>(async () => {
	const elem = await import(
		"./tabs/Transactions" /* webpackChunkName: "archives-page-transactions" */
	);
	return { default: elem.default };
});

const LazyBankTransactions = lazy<React.FC<ArchivesPage.Props>>(async () => {
	const elem = await import(
		"./tabs/BankTransactions" /* webpackChunkName: "archives-page-bank-transactions" */
	);
	return { default: elem.default };
});

const LazyCallsHistory = lazy<React.FC<ArchivesPage.Props>>(async () => {
	const elem = await import(
		"./tabs/CallsHistory" /* webpackChunkName: "archives-page-calls-history" */
	);
	return { default: elem.default };
});

const LazyReceiptsHistory = lazy<React.FC<ArchivesPage.Props>>(async () => {
	const elem = await import(
		"./tabs/ReceiptsHistory" /* webpackChunkName: "archives-page-receipts-history" */
	);
	return { default: elem.default };
});

const ArchivesPage: React.FC = () => {
	const { t } = useTranslation();
	const dispatch = useTypedDispatch();

	const personalRole = useTypedSelector(
		(state) => state.account.personalRole,
	);
	const ratesSubscription = useModelSubscribe({}, Tariff);

	const models = useMemo<Tariff.Model[]>(
		() => ratesSubscription.models,
		[ratesSubscription],
	);

	useEffect(() => {
		if (models?.length) dispatch(setRates(models));
	}, [dispatch, models]);

	const tabs: TabOptions.Array = useMemo(
		() =>
			[
				{
					key: TabKeys.ORDERS,
					label: t("mainPage.archives.orders.title"),
					value: {
						render: () => (
							<Suspense
								fallback={
									<StyledRow
										position="absolute"
										top="50%"
										left="50%"
									>
										<SuspenseLoader />
									</StyledRow>
								}
							>
								<LazyOrders
									headerTitle={t(
										"mainPage.archives.orders.title",
									)}
								/>
							</Suspense>
						),
					},
					accessName: TabAccessNames[TabKeys.ORDERS],
				},
				{
					key: TabKeys.BANK_TRANSACTIONS,
					label: t("mainPage.archives.bankTransactions.title"),
					value: {
						render: () => (
							<Suspense
								fallback={
									<StyledRow
										position="absolute"
										top="50%"
										left="50%"
									>
										<SuspenseLoader />
									</StyledRow>
								}
							>
								<LazyBankTransactions
									headerTitle={t(
										"mainPage.archives.bankTransactions.title",
									)}
								/>
							</Suspense>
						),
					},
					accessName: TabAccessNames[TabKeys.BANK_TRANSACTIONS],
				},
				{
					key: TabKeys.TRANSACTIONS,
					label: t("mainPage.archives.transactions.title"),
					value: {
						render: () => (
							<Suspense
								fallback={
									<StyledRow
										position="absolute"
										top="50%"
										left="50%"
									>
										<SuspenseLoader />
									</StyledRow>
								}
							>
								<LazyTransactions
									headerTitle={t(
										"mainPage.archives.transactions.title",
									)}
								/>
							</Suspense>
						),
					},
					accessName: TabAccessNames[TabKeys.TRANSACTIONS],
				},
				{
					key: TabKeys.GPS_LOG,
					label: t("mainPage.archives.gpsLog.title"),
					value: {
						render: () => (
							<Suspense
								fallback={
									<StyledRow
										position="absolute"
										top="50%"
										left="50%"
									>
										<SuspenseLoader />
									</StyledRow>
								}
							>
								<LazyGPSLog
									headerTitle={t(
										"mainPage.archives.gpsLog.title",
									)}
								/>
							</Suspense>
						),
					},
					accessName: TabAccessNames[TabKeys.GPS_LOG],
				},
				{
					key: TabKeys.MESSAGES,
					label: t("mainPage.archives.messages.title"),
					value: {
						render: () => (
							<Suspense
								fallback={
									<StyledRow
										position="absolute"
										top="50%"
										left="50%"
									>
										<SuspenseLoader />
									</StyledRow>
								}
							>
								<LazyMessagesHistory
									headerTitle={t(
										"mainPage.archives.messages.title",
									)}
								/>
							</Suspense>
						),
					},
					accessName: TabAccessNames[TabKeys.MESSAGES],
				},
				{
					key: TabKeys.PUSH,
					label: t("mainPage.archives.push.title"),
					value: {
						render: () => (
							<Suspense
								fallback={
									<StyledRow
										position="absolute"
										top="50%"
										left="50%"
									>
										<SuspenseLoader />
									</StyledRow>
								}
							>
								<LazyPushHistory
									headerTitle={t(
										"mainPage.archives.push.title",
									)}
								/>
							</Suspense>
						),
					},
					accessName: TabAccessNames[TabKeys.PUSH],
				},

				{
					key: TabKeys.RECEIPTS_HISTORY,
					label: t("mainPage.archives.receiptsHistory.title"),
					value: {
						render: () => (
							<Suspense
								fallback={
									<StyledRow
										position="absolute"
										top="50%"
										left="50%"
									>
										<SuspenseLoader />
									</StyledRow>
								}
							>
								<LazyReceiptsHistory
									headerTitle={t(
										"mainPage.archives.receiptsHistory.title",
									)}
								/>
							</Suspense>
						),
					},
					accessName: TabAccessNames[TabKeys.RECEIPTS_HISTORY],
				},
				{
					key: TabKeys.CALLS_HISTORY,
					label: t("mainPage.archives.callsHistory.title"),
					value: {
						render: () => (
							<Suspense
								fallback={
									<StyledRow
										position="absolute"
										top="50%"
										left="50%"
									>
										<SuspenseLoader />
									</StyledRow>
								}
							>
								<LazyCallsHistory
									headerTitle={t(
										"mainPage.archives.callsHistory.title",
									)}
								/>
							</Suspense>
						),
					},
					accessName: TabAccessNames[TabKeys.CALLS_HISTORY],
				},
			].filter((btn) => hasAccess(btn.accessName, personalRole)),
		[t, personalRole],
	);

	const { activeKey, setActiveKey } = useActiveTab(tabs);
	const [visitedKeys, setVisitedKeys] = useState<Key[]>([]);

	useEffect(() => {
		if (!activeKey) return;
		setVisitedKeys((prev) =>
			prev.includes(activeKey) ? prev : [...prev, activeKey],
		);
	}, [activeKey]);

	const tabContent = useMemo(
		() => (
			<div
				style={{
					position: "relative",
					width: "100%",
					height: "100%",
					overflow: "hidden",
				}}
			>
				{tabs.map((tab) => {
					const isActive = tab.key === activeKey;
					const isVisited =
						isActive || visitedKeys.includes(tab.key);
					if (!isVisited) return null;

					return (
						<div
							key={tab.key}
							style={{
								display: isActive ? "flex" : "none",
								flexDirection: "column",
								width: "100%",
								height: "100%",
								overflow: "hidden",
							}}
						>
							{tab.value.render()}
						</div>
					);
				})}
			</div>
		),
		[tabs, activeKey, visitedKeys],
	);

	return (
		<Suspense
			fallback={
				<StyledRow position="absolute" top="50%" left="50%">
					<SuspenseLoader />
				</StyledRow>
			}
		>
			<BasicPageLayout
				navigation={
					<SideTab
						value={activeKey}
						onChange={(newActive) => {
							setActiveKey(newActive);
						}}
						options={tabs}
						variant="vertical"
					/>
				}
				content={tabContent}
			/>
		</Suspense>
	);
};

declare namespace ArchivesPage {
	interface Props {
		headerTitle: string;
	}
}

export default ArchivesPage;
